import dynamic from 'next/dynamic'
import isEmpty from 'lodash/isEmpty'
import { useEffect, useRef, useState } from 'react'
import { ChartContainer, useTheme } from '~/design_system'
import { useChartFormat, useFormattedTitle } from '~/design_system/components/charts/format'
import { SadStates } from '~/components/SadStates'
// each country needs its own geo data to render the choropleth chart
// follow a similar shape as the existing geo data if introducing a new country
import {
  CustomLinkChartProps,
  Datum,
  GenericChartProps,
  ReportKeyChartProps
} from '~/design_system/components/charts/ChartTypes'
import { CustomChoroplethLegend } from '~/design_system/components/charts/CustomChoroplethLegend'
import { DefaultChartSkeleton } from '~/design_system/components/charts/DefaultChartSkeleton'
import { useMsg } from '~/common/localizationUtils'
import geoData from './geodata/us_states.json'

// nivo elements have to be dynamically imported because d3 went ESM only and nextjs doesn't like it
const ResponsiveChoropleth = dynamic(() => import('@nivo/geo').then(m => m.ResponsiveChoropleth), { ssr: false })
const ThemeProvider = dynamic(() => import('@nivo/core').then(m => m.ThemeProvider), { ssr: false })

/** Calculate domain (range of all possible values) for the given data */
const getDomain = (data: Datum[]) => {
  const values = data.map(obj => (typeof obj.value === 'string' ? parseFloat(obj.value) : obj.value))
  return [Math.min(...values), Math.max(...values)]
}

interface BaseCountryDataChoroplethProps extends GenericChartProps {
  projectionScale?: number
}

interface CustomLinkCountryDataChoroplethProps extends BaseCountryDataChoroplethProps, CustomLinkChartProps {}

interface ReportKeyCountryDataChoroplethProps extends BaseCountryDataChoroplethProps, ReportKeyChartProps {}

export type CountryDataChoroplethProps = CustomLinkCountryDataChoroplethProps | ReportKeyCountryDataChoroplethProps

export const CountryDataChoropleth = ({
  data,
  chartConfig,
  reportKey,
  customReportUrl,
  // projection values differ based on the container width & height
  projectionScale: defaultProjectionScale,
  isLoading = true,
  isFetching = true,
  isError,
  errorCode
}: CountryDataChoroplethProps) => {
  const msg = useMsg()
  const { title, subTitle = msg('dashboards.chart.us_states_subtitle'), format = 'number' } = chartConfig
  const { colors } = useTheme()
  const purples: string[] = Object.values(colors.purple)
  const valueFormat = useChartFormat('number')

  const containerRef = useRef<HTMLDivElement>(null)
  const DEFAULT_SCALE = 600
  const [projectionScale, setProjectionScale] = useState(DEFAULT_SCALE)

  useEffect(() => {
    // Choropleth width/scale = parent width rounded down to nearest 50
    // eg. parent width = 473, choropleth width will be = 450
    const scale = Math.min(Math.floor((containerRef.current?.offsetWidth || DEFAULT_SCALE) / 50) * 50, 600)
    /*
      eslint-disable-next-line react-hooks/set-state-in-effect --

      ⚠️ HEY YOU, DEV! Yes, YOU! Don't you look away...I know you are reading this. This is your problem now.
      Fix the below while you are working in here. It's simple, just delete this comment, and you'll see the lint error.
      There's even a suggestion on how you might fix it. These particular lint errors are serious issues that we
      couldn't feasibly fix across the whole codebase, but they need fixed. Your help is appreciated! 🥳

      Issue: Setting projectionScale from ref measurement causes extra render.
      Possible fix approach: Use useLayoutEffect for DOM measurements, or use a ResizeObserver
      with useCallback to update scale without effect-based state.
    */
    setProjectionScale(scale)
  }, [containerRef])

  return (
    <div ref={containerRef}>
      <ChartContainer
        title={useFormattedTitle(title, format)}
        reportKey={reportKey}
        customReportUrl={customReportUrl}
        subTitle={subTitle}
        isLoading={isLoading}
        isFetching={isFetching}
        isEmpty={data === undefined || isEmpty(data)}
        isError={errorCode === 403 && data === undefined ? false : isError}>
        <SadStates states={[{ when: isLoading, render: () => <DefaultChartSkeleton /> }]}>
          {data && (
            <>
              <ResponsiveChoropleth
                features={geoData}
                data={data}
                domain={getDomain(data)}
                projectionScale={defaultProjectionScale || projectionScale}
                projectionRotation={[97, -36, 1]} // Rotates USA into view (think "rotating a globe")
                projectionTranslation={[0.5, 0.5]} // [.5, .5] will "center" the US in the available width/height
                colors={purples.slice(1, 8)}
                unknownColor="#fff"
                borderWidth={0.5}
                borderColor="grey"
                label={obj => obj.properties.name}
                valueFormat={valueFormat}
              />
              <ThemeProvider theme={{ legends: { text: { fontSize: 16 } } }}>
                <CustomChoroplethLegend data={data} colors={purples} valueFormat={valueFormat} />
              </ThemeProvider>
            </>
          )}
        </SadStates>
      </ChartContainer>
    </div>
  )
}
